import React, { useCallback, useMemo, useState } from 'react'
import { NavigateFunction, To } from 'react-router-dom'
import queryString, { ParsedQuery } from 'query-string'
import log from 'loglevel'
import {
  EvidenceGroup,
  GetControlEvidenceTabRequest,
} from '@trustero/trustero-api-web/lib/attachment/attachment_pb'
import { AttachmentPromiseClient } from '@trustero/trustero-api-web/lib/attachment/attachment_grpc_web_pb'
import { GravatarWithTooltip } from 'src/components/Gravatar/Gravatar'
import { FilterBar } from 'src/components/Reusable/IndexPage/FilterBar/FilterBar'
import { FilterBarConfigs } from 'src/components/Reusable/IndexPage/FilterBar/FilterBar.constants'
import { INDEX_PAGE_WITH_FILTERS } from 'src/components/Reusable/IndexPage/IndexPage.constants'
import { CustomFileTypeFilterModal } from 'src/components/Reusable/IndexPage/FilterBar/modals/CustomFileTypeFilterModal'
import { FileTypeFilterProvider } from 'src/pages/Evidence/context/FileTypeFilterContext'
import {
  NavigateProvider,
  useNavigateContext,
} from 'src/components/Reusable/Navigation/NavigateContext'
import { SearchProvider } from 'src/Utils/search/search.context'
import { useApplySearch } from 'src/Utils/search/search.hooks'
import { prepareSearchableData } from 'src/Utils/search/search.helpers'
import { getEvidenceSource } from 'src/Utils/Evidence/evidence.helpers'
import { useFileTypeIconForEvidenceGroups } from 'src/components/ModalForms/FileType/useFileTypeIcon'
import { EvidenceIndexGridHeader } from 'src/pages/Evidence/grid/EvidenceGridHeader'
import { DATE_FORMATS } from 'src/Utils/dateConstants'
import { formatISODateTime, formatTimestamp } from 'src/Utils/formatDate'
import { formatEvidenceCaption } from 'src/Utils/helpers/string.helpers'
import {
  MultiSelectModal,
  MultiSelectState,
} from 'src/components/Reusable/MultiSelectModal'
import { ModalFormId } from 'src/components/ModalForms/ModalForm'
import { useGrpcRevalidateByMethod } from 'src/components/async'
import { useHideModal, useIsShowModal } from 'src/Modal/ModalStateContext'
import { showInfoToast } from 'src/Utils/helpers/toast'
import {
  useControlEvidenceTabRequest,
  useLatestEvidenceGroups,
  useLatestEvidenceGroupsRequestFromParsedQuery,
  useLinkEvidenceToControl,
} from 'src/pages/Evidence/evidence.hooks'
import { EvidenceCellText } from './LinkEvidenceToControlModal.styles'

const useSearchableLatestEvidenceGroups = (evidenceGroups: EvidenceGroup[]) =>
  useMemo(
    () =>
      prepareSearchableData(
        evidenceGroups,
        (group) => group.getEvidenceId(),
        (group) => {
          const source = getEvidenceSource(
            group.getIsAutomated(),
            group.getIsLinkedDocument(),
            group.getServiceModelId(),
            group.getDocImportLink(),
            group.getMime(),
          )
          const sourceText =
            typeof source === 'string' ? source : 'View in Source'

          return [
            group.getCaption(),
            sourceText,
            group.getActorEmail(),
            group.getRelevantDate()
              ? formatISODateTime(group.getRelevantDate())
              : '',
          ]
        },
        (group) => [group.getContentId() || ''],
      ),
    [evidenceGroups],
  )

/**
 * Modal that links existing account evidence to a control. Presents every
 * evidence object in the account (minus those already linked to this control)
 * in a filterable / searchable multi-select list, reusing the evidence index's
 * FilterBar + columns. Filter/search state is isolated to the modal via a
 * NavigateProvider that writes query params to local state instead of the page
 * URL.
 */
export const LinkEvidenceToControlModal = ({
  controlId,
  controlName,
}: {
  controlId: string
  controlName?: string
}): JSX.Element => {
  const show = useIsShowModal(ModalFormId.LINK_EVIDENCE_TO_CONTROL)
  const [parsedQueryParams, setParsedQueryParams] = useState<ParsedQuery>({})
  // Clear the modal's local filter/search state on close so a fresh open always
  // starts unfiltered.
  const hide = useHideModal({
    modalId: ModalFormId.LINK_EVIDENCE_TO_CONTROL,
    onHide: () => setParsedQueryParams({}),
  })

  const navigate: NavigateFunction = useCallback((to: To | number) => {
    if (typeof to === 'number') {
      return
    }
    const search =
      typeof to === 'string'
        ? new URL(to, window.location.origin).search
        : to.search || ''
    setParsedQueryParams(queryString.parse(search, { arrayFormat: 'bracket' }))
  }, [])

  const locationSearchString = useMemo(() => {
    const search = queryString.stringify(parsedQueryParams, {
      arrayFormat: 'bracket',
    })
    return search && `?${search}`
  }, [parsedQueryParams])

  return (
    <NavigateProvider
      navigate={navigate}
      parsedQueryParams={parsedQueryParams}
      locationSearchString={locationSearchString}
    >
      <SearchProvider>
        <LinkEvidenceToControlModalContent
          controlId={controlId}
          controlName={controlName}
          show={show}
          hide={hide}
        />
      </SearchProvider>
    </NavigateProvider>
  )
}

const LinkEvidenceToControlModalContent = ({
  controlId,
  controlName,
  show,
  hide,
}: {
  controlId: string
  controlName?: string
  show: boolean
  hide: () => void
}): JSX.Element => {
  const {
    navigateState: { parsedQueryParams },
  } = useNavigateContext()
  const req = useLatestEvidenceGroupsRequestFromParsedQuery(
    parsedQueryParams ?? {},
  )
  const fileTypeIcon = useFileTypeIconForEvidenceGroups()
  const revalidate = useGrpcRevalidateByMethod()
  const linkEvidenceToControl = useLinkEvidenceToControl()

  // Only fetch while the modal is open.
  const { data, isLoading, error } = useLatestEvidenceGroups(req, show)

  // Evidence already linked to this control, to exclude from the list.
  const controlEvidenceReq = useMemo(
    () => new GetControlEvidenceTabRequest().setControlModelId(controlId),
    [controlId],
  )
  const { data: linkedData } = useControlEvidenceTabRequest(
    controlEvidenceReq,
    show,
  )
  const linkedEvidenceIds = useMemo(
    () =>
      new Set(
        (linkedData?.getItemsList() ?? []).map((wrapper) =>
          wrapper.getEvidenceId(),
        ),
      ),
    [linkedData],
  )

  const evidenceGroups = useMemo(
    () =>
      (data?.getItemsList() ?? [])
        .map((wrapper) => wrapper.getEvidenceGroup())
        .filter((group): group is EvidenceGroup => !!group)
        .filter((group) => !linkedEvidenceIds.has(group.getEvidenceId())),
    [data, linkedEvidenceIds],
  )

  const searchableEvidence = useSearchableLatestEvidenceGroups(evidenceGroups)
  const { data: matchingEvidenceGroups } = useApplySearch(searchableEvidence)

  const items = useMemo(
    () =>
      matchingEvidenceGroups.map((group) => {
        const FileTypeIcon = fileTypeIcon(
          group.getIsAutomated(),
          group.getMime(),
          group.getIsLinkedDocument(),
          group.getServiceModelId(),
        )
        const relevantDate = group.getRelevantDate()
        const createdAt = group.getCreatedAt()

        return {
          id: group.getEvidenceId(),
          renderItems: [
            <FileTypeIcon width="24px" height="24px" key="0-icon" />,
            <EvidenceCellText key="1-title">
              {
                formatEvidenceCaption(
                  group.getCaption(),
                  group.getIsAutomated(),
                ) as string
              }
            </EvidenceCellText>,
            <EvidenceCellText key="2-controls-count">
              {group.getLinkedControlsCount() || 0}
            </EvidenceCellText>,
            <GravatarWithTooltip
              email={group.getActorEmail()}
              name={group.getActorName()}
              key="3-actor"
            />,
            relevantDate ? (
              <EvidenceCellText key="4-relevant-date">
                {formatTimestamp(relevantDate, DATE_FORMATS.ISO_WITH_TIME)}
              </EvidenceCellText>
            ) : null,
            createdAt ? (
              <EvidenceCellText key="5-created-at">
                {formatTimestamp(createdAt, DATE_FORMATS.ISO_WITH_TIME)}
              </EvidenceCellText>
            ) : null,
          ],
        }
      }),
    [matchingEvidenceGroups, fileTypeIcon],
  )

  const filters = useMemo(
    () => (
      <FileTypeFilterProvider>
        <>
          <FilterBar
            config={FilterBarConfigs[INDEX_PAGE_WITH_FILTERS.EVIDENCE]}
            searchBarOptions={{ withContentSearch: true }}
          />
          <CustomFileTypeFilterModal />
        </>
      </FileTypeFilterProvider>
    ),
    [],
  )

  const hasFilteredResults = useMemo(
    () => !!parsedQueryParams && Object.keys(parsedQueryParams).length > 0,
    [parsedQueryParams],
  )

  const currentSelection = useMemo<MultiSelectState>(
    () => ({ includeAll: false, included: new Set<string>() }),
    [],
  )

  const onCommit = useCallback(
    async (selection: MultiSelectState) => {
      const evidenceIds = selection.includeAll
        ? items.map((item) => item.id)
        : Array.from(selection.included ?? [])
      if (!evidenceIds.length) {
        return
      }
      try {
        await linkEvidenceToControl(controlId, evidenceIds)
        await revalidate(
          AttachmentPromiseClient.prototype.getControlEvidenceTabView,
        )
      } catch (err) {
        log.error('Error linking evidence to control', err)
        showInfoToast('Error linking evidence to control')
      }
    },
    [items, linkEvidenceToControl, controlId, revalidate],
  )

  return (
    <MultiSelectModal
      title="Link Evidence"
      description={
        controlName
          ? `The selected evidence will be linked to ${controlName}.`
          : 'The selected evidence will be linked to this control.'
      }
      itemType="evidence"
      currentSelection={currentSelection}
      onCommit={onCommit}
      items={items}
      numberOfColumns={6}
      show={show}
      hide={hide}
      isLoading={isLoading}
      error={error}
      filters={filters}
      isFilteredResults={hasFilteredResults}
      modalSize="xl"
      headers={
        items.length ? <EvidenceIndexGridHeader isReadonly={true} /> : null
      }
      formId={ModalFormId.LINK_EVIDENCE_TO_CONTROL}
      submitText="Link Evidence"
    />
  )
}
